import { ArdoqId, APIEntityType } from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { type I18nLocale } from '@ardoq/i18n';
import { isParentOfLabel } from './parentChildLabels';
import { ItemLabels, RawGraphEdge2, RawGraphNode2 } from '../types';
import {
  getOtherLabelsForComponent,
  getOtherLabelsDataForComponent,
} from './getOtherLabelsForComponent';
import {
  getOtherLabelsDataForReference,
  getOtherLabelsForReference,
} from './getOtherLabelsForReference';
import { getComponentLabelParts } from '../truncateText/getComponentLabelParts';
import { truncateComponentLabel } from '../truncateText/truncateComponentLabel';
import {
  LEGACY_NODE_LABEL_MAX_WIDTH,
  measureNodeLabelWidth,
} from './legacyLabelFormattingUtils';
import {
  getLabelsInterfaceWithModelInterfaces,
  LabelsInterface,
} from '../labelsInterface/labelsInterface';
import { returnZero } from '@ardoq/common-helpers';
import { getGraphInterfaceWithModelInterfaces } from '../graphInterface/graphInterface';
import { getFirstFormattedMultiLabel } from './getFirstFormattedMultiLabel';

// #region labels for model types

export const getNodeMainLabel = (
  rawGraphItem: RawGraphNode2,
  isComponent: boolean
): string => {
  const componentId = isComponent && rawGraphItem.modelId;

  return componentId
    ? truncateComponentLabel({
        ...getComponentLabelParts(componentId),
        measure: returnZero,
        width: Infinity,
      })
    : (rawGraphItem.label ?? '');
};

const getParentChildReferenceMainLabel = (
  isShowReferenceTypeApplied: boolean,
  i18nLocale: I18nLocale
) => (isShowReferenceTypeApplied ? isParentOfLabel(i18nLocale) : '');

// #endregion

// #region labels for graph items

// #region nodes

export type GetLabelsDataInterface = LabelsInterface & {
  isComponent: (modelId: ArdoqId) => boolean;
};

export const getGraphNodeOrGroupLabelForViewpointMode = (
  labelsInterface: GetLabelsDataInterface,
  { modelId, subLabels, label }: RawGraphNode2,
  i18nLocale: I18nLocale
): ItemLabels => {
  const isComponent = modelId && labelsInterface.isComponent(modelId);

  const otherLabels = isComponent
    ? getOtherLabelsForComponent(labelsInterface, modelId, i18nLocale)
    : undefined;

  const mainLabel =
    (isComponent ? labelsInterface.getComponentDisplayName(modelId) : label) ??
    '';

  return {
    mainLabel,
    subLabel: subLabels?.join(', '),
    otherLabels,
  };
};

const getFirstFormattedLabelForComponent = (
  labelsInterface: GetLabelsDataInterface,
  componentId: ArdoqId,
  i18nLocale: I18nLocale
) => {
  const labelsData = getOtherLabelsDataForComponent(
    labelsInterface,
    componentId
  );
  if (!labelsData) return null;
  return getFirstFormattedMultiLabel(
    labelsInterface,
    componentId,
    labelsData,
    APIEntityType.COMPONENT,
    i18nLocale
  );
};

export const getGraphNodeOrGroupLabelForViewsNotSupportingMultilabeling = (
  labelsInterface: GetLabelsDataInterface,
  { modelId, subLabels, label }: RawGraphNode2,
  i18nLocale: I18nLocale
): ItemLabels => {
  const isComponent = modelId && labelsInterface.isComponent(modelId);

  const mainLabel =
    (isComponent ? labelsInterface.getComponentDisplayName(modelId) : label) ??
    '';

  if (!isComponent) {
    return { mainLabel, subLabel: subLabels?.join(', ') };
  }

  const firstLabel = getFirstFormattedLabelForComponent(
    labelsInterface,
    modelId,
    i18nLocale
  );

  return {
    mainLabel,
    subLabel: subLabels?.join(', '),
    otherLabels: firstLabel ? [firstLabel] : undefined,
  };
};

export const getGraphNodeOrGroupLabelForWorkspaceMode = (
  rawItem: RawGraphNode2
): ItemLabels => {
  const { modelId, subLabels, label, name } = rawItem;

  const isComponent = modelId && componentInterface.isComponent(modelId);

  const legacyLabelParts = isComponent
    ? getComponentLabelParts(modelId)
    : undefined;

  const legacyTruncatedFieldValueAndLabelForNode = legacyLabelParts?.fieldValue
    ? truncateComponentLabel({
        ...legacyLabelParts,
        label: '',
        measure: measureNodeLabelWidth,
        width: LEGACY_NODE_LABEL_MAX_WIDTH,
      })
    : undefined;

  const mainLabel =
    (isComponent
      ? (legacyLabelParts?.label ??
        name ??
        componentInterface.getDisplayName(modelId))
      : label) ?? '';

  return {
    mainLabel,
    subLabel: subLabels?.join(', '),
    legacyLabelParts,
    legacyTruncatedFieldValueAndLabelForNode,
  };
};

/**
 * labels and sub-labels for groups are set in buildGroups and are already present
 * in the rawGraphItem.
 * In case of (isMultiLabel && isComponent), the main label is the component name,
 * otherwise the name set by the grouping.
 */
export const getGraphNodeOrGroupLabel = (
  rawItem: RawGraphNode2,
  isMultiLabel: boolean,
  i18nLocale: I18nLocale
): ItemLabels => {
  if (isMultiLabel) {
    const labelsInterface = {
      ...getLabelsInterfaceWithModelInterfaces(),
      ...getGraphInterfaceWithModelInterfaces(),
    };
    return getGraphNodeOrGroupLabelForViewpointMode(
      labelsInterface,
      rawItem,
      i18nLocale
    );
  }

  return getGraphNodeOrGroupLabelForWorkspaceMode(rawItem);
};

// #endregion

// #region edges

export const getGraphEdgeLabelForViewpointMode = (
  labelsInterface: LabelsInterface,
  rawGraphItem: RawGraphEdge2,
  i18nLocale: I18nLocale,
  isShowReferenceTypeApplied = false
): ItemLabels => {
  const {
    id: referenceId,
    modelId: referenceModelId,
    modelIds: referenceModelIds,
    isParentChildReference,
  } = rawGraphItem;

  if (isParentChildReference) {
    return {
      mainLabel: getParentChildReferenceMainLabel(
        isShowReferenceTypeApplied,
        i18nLocale
      ),
    };
  }

  const [firstReferenceModelId] = referenceModelIds ?? [];
  const labelSourceId =
    firstReferenceModelId ?? referenceModelId ?? referenceId;
  const mainLabel = isShowReferenceTypeApplied
    ? (labelsInterface.getGlobalReferenceType(labelSourceId)?.name ?? '')
    : '';

  if (referenceModelIds) {
    // the references will only be aggregated together if their other labels are the same — use the first
    const otherLabels = getOtherLabelsForReference(
      labelsInterface,
      firstReferenceModelId,
      i18nLocale
    );
    return { mainLabel, otherLabels };
  }

  const otherLabels = getOtherLabelsForReference(
    labelsInterface,
    referenceId,
    i18nLocale
  );
  return { mainLabel, otherLabels };
};

const getFirstFormattedLabelForReference = (
  labelsInterface: LabelsInterface,
  referenceModelId: ArdoqId,
  i18nLocale: I18nLocale
) => {
  const labelsData = getOtherLabelsDataForReference(
    labelsInterface,
    referenceModelId
  );
  if (!labelsData) return null;
  return getFirstFormattedMultiLabel(
    labelsInterface,
    referenceModelId,
    labelsData,
    APIEntityType.REFERENCE,
    i18nLocale
  );
};

export const getGraphEdgeLabelForViewsNotSupportingMultilabeling = (
  labelsInterface: LabelsInterface,
  rawGraphItem: RawGraphEdge2,
  i18nLocale: I18nLocale,
  isShowReferenceTypeApplied = false
): ItemLabels => {
  const {
    modelId,
    modelIds: aggregatedModelIds,
    isParentChildReference,
  } = rawGraphItem;

  if (isParentChildReference) {
    return {
      mainLabel: getParentChildReferenceMainLabel(
        isShowReferenceTypeApplied,
        i18nLocale
      ),
    };
  }

  if (aggregatedModelIds) {
    // aggregated refs share other labels — use the first
    const [firstAggregatedModelId] = aggregatedModelIds;
    const mainLabel = isShowReferenceTypeApplied
      ? (labelsInterface.getGlobalReferenceType(firstAggregatedModelId)?.name ??
        '')
      : '';
    const firstLabel = getFirstFormattedLabelForReference(
      labelsInterface,
      firstAggregatedModelId,
      i18nLocale
    );
    return { mainLabel, otherLabels: firstLabel ? [firstLabel] : undefined };
  }

  // modelId is always set for non-aggregated, non-parent-child references —
  // getResultingReferenceList always assigns modelId: referenceId
  const mainLabel = isShowReferenceTypeApplied
    ? (labelsInterface.getGlobalReferenceType(modelId!)?.name ?? '')
    : '';
  const firstLabel = getFirstFormattedLabelForReference(
    labelsInterface,
    modelId!,
    i18nLocale
  );
  return { mainLabel, otherLabels: firstLabel ? [firstLabel] : undefined };
};

export const getWorkspaceEdgeLabelReferenceId = ({
  id,
  modelId,
  modelIds,
  isParentChildReference,
}: RawGraphEdge2): ArdoqId | null => {
  if (isParentChildReference) return null;
  if (modelIds) return modelIds[0] ?? null;
  return modelId || id;
};

export const getGraphEdgeLabelForWorkspaceMode = (
  rawGraphItem: RawGraphEdge2,
  resolvedReferenceDisplayLabel: string | null,
  i18nLocale: I18nLocale,
  isShowReferenceTypeApplied = false
): ItemLabels => {
  const { modelIds: referenceModelIds, isParentChildReference } = rawGraphItem;

  if (isParentChildReference) {
    return {
      mainLabel: getParentChildReferenceMainLabel(
        isShowReferenceTypeApplied,
        i18nLocale
      ),
    };
  }

  if (referenceModelIds) {
    return {
      mainLabel: resolvedReferenceDisplayLabel ?? '',
    };
  }

  return {
    mainLabel: resolvedReferenceDisplayLabel,
  };
};

// #endregion

// #endregion labels for graph items
